import { Card, Group, SimpleGrid, Stack, Text, ThemeIcon } from '@mantine/core';
import {
  IconBolt,
  IconBrandDiscord,
  IconBrandSlack,
  IconBuildingStore,
  IconForms,
  IconMail,
  IconMailForward,
  IconPlugConnected,
  IconTable,
  IconWebhook,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import type { AdapterMeta } from './types';

/**
 * Explicit icon map for the adapter registry. Listed by hand instead of
 * `import * as TablerIcons` so Vite can tree-shake the rest of the icon
 * package — the wildcard form pulled in 2.6MB of unused icons and broke the
 * workbox precache build.
 *
 * Add new entries here when extending `ADAPTER_REGISTRY` with a new `icon`
 * value. Unknown names fall back to `IconPlugConnected`.
 */
const ICON_MAP: Record<string, React.ComponentType<{ size?: number }>> = {
  webhook: IconWebhook,
  bolt: IconBolt,
  'brand-slack': IconBrandSlack,
  'brand-discord': IconBrandDiscord,
  table: IconTable,
  mail: IconMail,
  forms: IconForms,
  'building-store': IconBuildingStore,
  'mail-forward': IconMailForward,
};

export interface AdapterPickerGridProps {
  adapters: AdapterMeta[];
  selected?: string;
  onSelect: (kind: string) => void;
}

/**
 * Renders the available adapters as a clickable card grid.
 *
 * Icons are looked up by name from `@tabler/icons-react` so the registry can
 * stay JSON-friendly. Unknown icon names fall back to `IconPlugConnected`.
 */
export function AdapterPickerGrid({ adapters, selected, onSelect }: AdapterPickerGridProps) {
  const { t } = useTranslation();

  return (
    <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
      {adapters.map((meta) => {
        const Icon = resolveIcon(meta.icon);
        const isSelected = selected === meta.kind;
        return (
          <Card
            key={meta.kind}
            withBorder
            shadow={isSelected ? 'sm' : undefined}
            padding="md"
            radius="md"
            style={{
              cursor: 'pointer',
              borderColor: isSelected ? 'var(--mantine-color-blue-5)' : undefined,
            }}
            onClick={() => onSelect(meta.kind)}
          >
            <Stack gap="xs">
              <Group gap="sm" wrap="nowrap">
                <ThemeIcon variant="light" size={36} radius="md">
                  <Icon size={20} />
                </ThemeIcon>
                <Text fw={500}>{t(meta.label)}</Text>
              </Group>
              <Text size="sm" c="dimmed">
                {t(meta.description)}
              </Text>
            </Stack>
          </Card>
        );
      })}
    </SimpleGrid>
  );
}

function resolveIcon(name: string): React.ComponentType<{ size?: number }> {
  return ICON_MAP[name] ?? IconPlugConnected;
}
